/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import {ToastWrapperService} from '../utils/toast-wrapper-service/toast-wrapper-service';
import {timer} from 'rxjs';

@Component({
    selector: 'app-upload-progress',
    templateUrl: './upload-progress.component.html',
    styleUrls: ['./upload-progress.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UploadProgressComponent implements OnChanges {
    // Maximum file size, in bytes
    @Input() maxSize = 10485760;
    @Input() file: File;
    @Input() uploadEvent: HttpEvent<any>;
    @Input() uploadFailed: boolean;
    @Input() deletePermissions: boolean;
    @Input() isPrevious: boolean;
    @Output() deleteFile = new EventEmitter<any>();
    @Output() retryUpload = new EventEmitter<any>();
    @Output() downloadFile = new EventEmitter<string>();
    @Output() isOverlayOpen = new EventEmitter<boolean>();

    @Input() showOverlay: boolean;
    @Input() isDeleting: boolean;
    @Input() isDownloading = false;

    public progressStatus: string;
    public message: string;
    public icon: string;
    public loaded = 0;
    public total = 0;
    public progress = 0;
    public deleteIconTooltip = 'Delete file';

    public showDownloadButton: boolean;

    private readonly DOWNLOAD_BUTTON_DELAY = 1000;

    constructor(private toast: ToastWrapperService) {
    }

    public get extension(): string {
        if (this.file == null || this.file.name.indexOf('.') < 0) {
            return '';
        }

        return this.file.name.split('.').pop();
    }

    public get progressRounded(): number {
        return Math.round(this.progress);
    }

    download(fileName: string): void {
        if (!this.disableButtons()) {
            this.downloadFile.emit(fileName);
        }
    }

    upload(): void {
        if (this.file.size > this.maxSize) {
            this.toast.warning(`The maximum file size of ${this.convertBytesToMegabytes(this.maxSize)} MB has been exceeded.<br/>
                    <strong>Please upload a smaller file.</strong>`, 'Warning');
            this.deleteFile.emit(this.file);
            return;
        }

        if (!this.uploadFailed) {
            this.total = this.convertBytesToKilobytes(this.file.size);
            this.progressStatus = 'info';
            switch (this.uploadEvent.type) {
                case HttpEventType.Sent:
                    this.message = `Uploading... ${this.progressRounded}%`;
                    break;
                case HttpEventType.UploadProgress:
                    this.loaded = this.convertBytesToKilobytes(this.uploadEvent.loaded);
                    this.progress = this.uploadEvent.loaded / this.uploadEvent.total * 99;
                    this.message = `Uploading... ${this.progressRounded}%`;
                    break;
                case HttpEventType.ResponseHeader:
                case HttpEventType.Response:
                    this.progressBarStatus(this.uploadEvent.status);
                    break;
            }
        } else {
            this.progressStatus = 'error';
            this.progressBarStatus(400);
        }
    }

    restartUploadBar(): void {
        this.progress = 0;
        this.message = `Uploading... ${this.progressRounded}%`;
        this.uploadFailed = false;
        this.progressStatus = 'info';
        this.icon = null;
        this.loaded = 0;
    }

    progressBarStatus(status: number): void {
        if (status === 200 || status === 201) {
            this.progressStatus = 'success';
            this.message = 'Completed';
            this.icon = 'iwp-icon-gen_info_ok';
            this.progress = 100;
            this.loaded = this.total;
            this.manageDownloadButton();
        } else {
            this.progressStatus = 'error';
            this.message = 'Error while uploading';
            this.icon = 'iwp-icon-pc_info_warning';
            this.showDownloadButton = false;
        }
    }

    manageDownloadButton(): void {
        this.showDownloadButton = this.isPrevious;
        timer(this.DOWNLOAD_BUTTON_DELAY).subscribe(() => {
            this.showDownloadButton = true;
        });
    }

    convertBytesToKilobytes(bytes: number): number {
        return Math.round(bytes / 1024);
    }

    convertBytesToMegabytes(bytes: number): number {
        return Math.round(bytes / (1024 * 1024));
    }

    deleteClick(file: File): void {
        if (!this.disableButtons()) {
            this.deleteFile.emit(file);
        }
    }

    openOverlay(): void {
        if (!this.disableButtonsDelete()) {
            this.showOverlay = true;
            this.isOverlayOpen.emit(true);
        }

    }

    closeOverlay(): void {
        this.showOverlay = false;
        this.isOverlayOpen.emit(false);
    }

    isDeletingSpinner(): boolean {
        return this.isDeleting;
    }

    retryClick(file: File): void {
        this.restartUploadBar();
        this.retryUpload.emit(file);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['uploadEvent'] || changes['uploadFailed']) {
            this.upload();
        }
    }

    disableButtons(): boolean {
        return this.isDownloading;
    }

    disableButtonsDelete(): boolean {
        return this.isDownloading || this.isDeleting;
    }
}
